import { useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { AlertTriangle, ArrowRight, UsersRound, Tractor, ClipboardCheck, PackageOpen, CircleDollarSign, Archive, FileCheck2 } from "lucide-react";
import { Area, AreaChart, Bar, BarChart, CartesianGrid, ResponsiveContainer, Tooltip as RechartsTooltip, XAxis, YAxis } from "recharts";
import { GeographyFilter } from "@/components/GeographyFilter";
import { MetricCard } from "@/components/MetricCard";
import { PageHeader } from "@/components/PageHeader";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Progress } from "@/components/ui/progress";
import { operationalServices } from "@/services";
import type { DashboardSummary } from "@/services/contracts";

const fmt = (n: number | undefined | null) => {
  if (n === null || n === undefined) return "—";
  return n.toLocaleString("en-US", { maximumFractionDigits: 2 });
};

const fmtKg = (n: number | undefined | null) => {
  if (n === null || n === undefined) return "—";
  return `${fmt(n)} kg`;
};

const fmtCurrency = (n: number | undefined | null) => {
  if (n === null || n === undefined) return "—";
  return `UGX ${fmt(Math.round(n))}`;
};

export default function DashboardPage() {
  const [summary, setSummary] = useState<DashboardSummary | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [geoUnitId, setGeoUnitId] = useState<string | undefined>(undefined);
  const [dateFrom, setDateFrom] = useState("");
  const [dateTo, setDateTo] = useState("");

  const loadSummary = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const params: { geographic_unit_id?: string; date_from?: string; date_to?: string } = {};
      if (geoUnitId) params.geographic_unit_id = geoUnitId;
      if (dateFrom) params.date_from = dateFrom;
      if (dateTo) params.date_to = dateTo;
      const data = await operationalServices.dashboard.getSummary(params);
      setSummary(data);
    } catch (err: unknown) {
      const msg = err instanceof Error ? err.message : "Failed to load dashboard";
      setError(msg);
    } finally {
      setLoading(false);
    }
  }, [geoUnitId, dateFrom, dateTo]);

  useEffect(() => { loadSummary(); }, [loadSummary]);

  if (loading && !summary) {
    return (
      <div className="space-y-7" data-testid="dashboard-page">
        <PageHeader eyebrow="Management" title="Dashboard" description="Loading management summary from validated staging data..." />
        <div className="grid gap-4 sm:grid-cols-2 xl:grid-cols-4">
          {[0, 1, 2, 3].map((i) => (
            <Card key={i} className="animate-pulse"><CardContent className="p-5 h-[120px] bg-muted/30" /></Card>
          ))}
        </div>
      </div>
    );
  }

  if (error && !summary) {
    return (
      <div className="space-y-7" data-testid="dashboard-page">
        <PageHeader eyebrow="Management" title="Dashboard" description="Management reporting layer" />
        <Card className="border-destructive/30"><CardContent className="p-6 text-sm text-destructive" data-testid="dashboard-error">
          <AlertTriangle className="mr-2 inline size-4" />{error}
          <Button variant="outline" size="sm" className="ml-4" onClick={loadSummary}>Retry</Button>
        </CardContent></Card>
      </div>
    );
  }

  if (!summary) return null;

  const ex = summary.exceptions;
  const exceptionItems = [
    { label: "Farmers with unpaid balance", value: ex.farmers_with_unpaid_balance, link: "/commercial" },
    { label: "Pending payment requests", value: ex.pending_payment_requests, link: "/finance" },
    { label: "Low stock items", value: ex.low_stock_items, link: "/inventory" },
    { label: "Overdue follow-ups", value: ex.overdue_follow_ups, link: "/visits" },
    { label: "Agreements expiring soon", value: ex.agreements_expiring_soon, link: "/agreements" },
    { label: "Unsigned agreements", value: ex.unsigned_agreements, link: "/agreements" },
    { label: "Pending distributions", value: ex.pending_distributions, link: "/distributions" },
  ].filter((e) => e.value > 0);

  const farmerCards = [
    { label: "Total Farmers", value: fmt(summary.farmers.total), detail: `${summary.farmers.active} active`, link: "/farmers" },
    { label: "Farmers with Farms", value: fmt(summary.farmers.with_farms), detail: `${summary.farmers.without_farms} without farms`, link: "/farmers" },
    { label: "Active Agreements", value: fmt(summary.farmers.with_active_agreements), detail: "60/40 commercial", link: "/agreements" },
    { label: "Recently Visited", value: fmt(summary.farmers.recently_visited), detail: "completed visits", link: "/visits" },
  ];

  const commercialCards = [
    { label: "Total Delivered", value: fmtKg(summary.commercial.total_delivered_quantity), detail: `${summary.commercial.delivery_count} deliveries`, link: "/commercial" },
    { label: "Delivering Farmers", value: fmt(summary.commercial.unique_delivering_farmers), detail: "unique farmers", link: "/commercial" },
    { label: "Farmer Outstanding", value: fmtCurrency(summary.commercial.farmer_total_outstanding), detail: `${summary.commercial.farmers_with_outstanding} farmers`, link: "/commercial" },
    { label: "Company Share (40%)", value: fmtCurrency(summary.commercial.company_cumulative_share), detail: "ACZON entitlement", link: "/commercial" },
  ];

  const operationCards = [
    { label: "Total Farms", value: fmt(summary.farms_plots.total_farms), detail: `${fmt(summary.farms_plots.total_plots)} plots`, link: "/farms" },
    { label: "Farm Area", value: `${fmt(summary.farms_plots.total_farm_area_acres)} ac`, detail: "reported area", link: "/farms" },
    { label: "Coffee Trees", value: fmt(summary.farms_plots.total_coffee_trees), detail: "estimated population", link: "/farms" },
    { label: "Active Staff", value: fmt(summary.hr.active_staff), detail: "project assignments", link: "/staff" },
  ];

  const fieldCards = [
    { label: "Field Visits", value: fmt(summary.field_operations.total_visits), detail: "completed", link: "/visits" },
    { label: "Offline-Originated", value: fmt(summary.field_operations.offline_visits), detail: `online: ${fmt(summary.field_operations.online_visits)}`, link: "/visits" },
    { label: "Overdue Follow-ups", value: fmt(summary.field_operations.overdue_follow_ups), detail: "past due date", link: "/visits" },
    { label: "Total Assets", value: fmt(summary.assets.total), detail: Object.entries(summary.assets.by_status).map(([k, v]) => `${k.toLowerCase()}:${v}`).join(", "), link: "/assets" },
  ];

  const formColors: Record<string, string> = { "DRY_CHERRY": "hsl(158 45% 28%)", "KIBOKO": "hsl(25 60% 40%)", "GREEN_BEAN": "hsl(200 50% 35%)", "OTHER": "hsl(0 0% 50%)" };

  return (
    <div className="space-y-7" data-testid="dashboard-page">
      <PageHeader
        eyebrow="Management reporting"
        title="Dashboard"
        description="Real-time management summary from validated staging source data. All KPIs reconcile to source-of-truth tables."
        action={<Button variant="outline" size="sm" onClick={loadSummary} data-testid="dashboard-refresh">Refresh</Button>}
      />

      <Card data-testid="dashboard-filters">
        <CardContent className="p-4">
          <div className="space-y-3">
            <GeographyFilter value={geoUnitId} onChange={setGeoUnitId} />
            <div className="flex flex-wrap items-end gap-3">
              <div className="space-y-1.5">
                <label className="text-xs font-medium text-muted-foreground">Date from (business event)</label>
                <input type="date" className="h-9 rounded-md border border-input bg-background px-3 text-sm" value={dateFrom} onChange={(e) => setDateFrom(e.target.value)} data-testid="dashboard-date-from" />
              </div>
              <div className="space-y-1.5">
                <label className="text-xs font-medium text-muted-foreground">Date to</label>
                <input type="date" className="h-9 rounded-md border border-input bg-background px-3 text-sm" value={dateTo} onChange={(e) => setDateTo(e.target.value)} data-testid="dashboard-date-to" />
              </div>
              {(geoUnitId || dateFrom || dateTo) && (
                <Button variant="ghost" size="sm" onClick={() => { setGeoUnitId(undefined); setDateFrom(""); setDateTo(""); }} data-testid="dashboard-clear-filters">Clear</Button>
              )}
            </div>
          </div>
        </CardContent>
      </Card>

      <section className="grid gap-4 sm:grid-cols-2 xl:grid-cols-4" aria-label="Farmer KPIs" data-testid="dashboard-farmer-kpis">
        {farmerCards.map((card) => (
          <Link key={card.label} to={card.link}>
            <MetricCard label={card.label} value={card.value} detail={card.detail} trend="" icon={UsersRound} testId={`dashboard-kpi-${card.label.toLowerCase().replace(/\s+/g, "-")}`} />
          </Link>
        ))}
      </section>

      <section className="grid gap-4 sm:grid-cols-2 xl:grid-cols-4" aria-label="Commercial KPIs" data-testid="dashboard-commercial-kpis">
        {commercialCards.map((card) => (
          <Link key={card.label} to={card.link}>
            <MetricCard label={card.label} value={card.value} detail={card.detail} trend="" icon={CircleDollarSign} tone="accent" testId={`dashboard-kpi-${card.label.toLowerCase().replace(/\s+/g, "-")}`} />
          </Link>
        ))}
      </section>

      <section className="grid gap-4 sm:grid-cols-2 xl:grid-cols-4" aria-label="Operations KPIs" data-testid="dashboard-operations-kpis">
        {operationCards.map((card) => (
          <Link key={card.label} to={card.link}>
            <MetricCard label={card.label} value={card.value} detail={card.detail} trend="" icon={Tractor} tone="neutral" testId={`dashboard-kpi-${card.label.toLowerCase().replace(/\s+/g, "-")}`} />
          </Link>
        ))}
      </section>

      <section className="grid gap-4 sm:grid-cols-2 xl:grid-cols-4" aria-label="Field Operations KPIs" data-testid="dashboard-field-kpis">
        {fieldCards.map((card) => (
          <Link key={card.label} to={card.link}>
            <MetricCard label={card.label} value={card.value} detail={card.detail} trend="" icon={ClipboardCheck} tone="warning" testId={`dashboard-kpi-${card.label.toLowerCase().replace(/\s+/g, "-")}`} />
          </Link>
        ))}
      </section>

      <section className="grid gap-5 xl:grid-cols-[minmax(0,1.45fr)_minmax(340px,0.75fr)]" data-testid="dashboard-chart-section">
        <Card data-testid="delivery-quantity-chart-card">
          <CardHeader className="flex flex-row items-start justify-between gap-4">
            <div>
              <CardTitle>Delivery quantity by product form</CardTitle>
              <CardDescription>Cumulative physical coffee received (Phase 5E source)</CardDescription>
            </div>
            <Badge variant="secondary" data-testid="delivery-quantity-badge">{fmt(summary.commercial.delivery_count)} deliveries</Badge>
          </CardHeader>
          <CardContent>
            <div className="h-[280px] w-full" data-testid="delivery-quantity-chart">
              <ResponsiveContainer width="100%" height="100%" minWidth={0} minHeight={200}>
                <BarChart data={summary.commercial.quantity_by_form.map((q) => ({ form: q.product_form, quantity: q.quantity }))} margin={{ left: -22, right: 6 }}>
                  <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="hsl(var(--border))" />
                  <XAxis dataKey="form" axisLine={false} tickLine={false} tick={{ fill: "hsl(var(--muted-foreground))", fontSize: 11 }} />
                  <YAxis axisLine={false} tickLine={false} tick={{ fill: "hsl(var(--muted-foreground))", fontSize: 11 }} />
                  <RechartsTooltip contentStyle={{ background: "hsl(var(--popover))", border: "1px solid hsl(var(--border))", borderRadius: 10, fontSize: 12 }} />
                  <Bar dataKey="quantity" name="Quantity (kg)" radius={[6, 6, 0, 0]}>
                    {summary.commercial.quantity_by_form.map((entry) => (
                      <Bar key={entry.product_form} dataKey="quantity" fill={formColors[entry.product_form] || "hsl(158 45% 28%)"} />
                    ))}
                  </Bar>
                </BarChart>
              </ResponsiveContainer>
            </div>
            <div className="mt-2 grid grid-cols-2 gap-2 text-xs sm:grid-cols-4">
              <div><span className="text-muted-foreground">Farmer due (60%):</span> <span className="font-semibold">{fmtCurrency(summary.commercial.farmer_cumulative_due)}</span></div>
              <div><span className="text-muted-foreground">Company share (40%):</span> <span className="font-semibold">{fmtCurrency(summary.commercial.company_cumulative_share)}</span></div>
              <div><span className="text-muted-foreground">Total paid:</span> <span className="font-semibold">{fmtCurrency(summary.commercial.farmer_total_paid)}</span></div>
              <div><span className="text-muted-foreground">Outstanding:</span> <span className="font-semibold text-destructive">{fmtCurrency(summary.commercial.farmer_total_outstanding)}</span></div>
            </div>
          </CardContent>
        </Card>

        <Card data-testid="expected-yield-card">
          <CardHeader>
            <CardTitle>Expected yield vs actual delivery</CardTitle>
            <CardDescription>Projection status and cumulative physical delivery</CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="rounded-lg border border-warning/20 bg-warning/10 p-3 text-sm" data-testid="expected-yield-status">
              <AlertTriangle className="mr-1.5 inline size-4 text-warning" />
              <span className="font-semibold">Expected yield: {summary.expected_yield.status}</span>
              <p className="mt-1.5 text-xs text-muted-foreground">{summary.expected_yield.note}</p>
            </div>
            <div className="space-y-2">
              <div className="flex items-center justify-between text-sm">
                <span className="text-muted-foreground">Project target (kg)</span>
                <span className="font-semibold">{fmt(summary.expected_yield.project_target_kg)}</span>
              </div>
              <div className="flex items-center justify-between text-sm">
                <span className="text-muted-foreground">Actual cumulative delivered</span>
                <span className="font-semibold">{fmtKg(summary.expected_yield.actual_cumulative_delivered)}</span>
              </div>
              {summary.expected_yield.project_target_kg && summary.expected_yield.project_target_kg > 0 && (
                <>
                  <div className="flex items-center justify-between text-sm">
                    <span className="text-muted-foreground">% delivered vs target</span>
                    <span className="font-semibold">{((summary.expected_yield.actual_cumulative_delivered / summary.expected_yield.project_target_kg) * 100).toFixed(1)}%</span>
                  </div>
                  <Progress value={(summary.expected_yield.actual_cumulative_delivered / summary.expected_yield.project_target_kg) * 100} className="h-2" data-testid="expected-yield-progress" />
                </>
              )}
            </div>
          </CardContent>
        </Card>
      </section>

      <section className="grid gap-5 xl:grid-cols-[minmax(0,1fr)_minmax(0,1fr)]" data-testid="dashboard-detail-section">
        <Card data-testid="dashboard-finance-summary">
          <CardHeader>
            <CardTitle>Finance summary</CardTitle>
            <CardDescription>Payment requests and posted journals (Phase 5D source)</CardDescription>
          </CardHeader>
          <CardContent className="space-y-3">
            <div className="grid grid-cols-2 gap-2">
              {Object.entries(summary.finance.payment_requests_by_status).map(([status, info]) => (
                <div key={status} className="rounded-lg border p-2.5 text-sm">
                  <div className="text-xs text-muted-foreground">{status}</div>
                  <div className="mt-0.5 font-semibold">{info.count} requests</div>
                  <div className="text-xs">{fmtCurrency(info.amount)}</div>
                </div>
              ))}
            </div>
            <div className="flex items-center justify-between rounded-lg border p-2.5 text-sm">
              <span className="text-muted-foreground">Posted journals</span>
              <span className="font-semibold">{fmt(summary.finance.posted_journals)}</span>
            </div>
            {summary.finance.pending_payment_requests > 0 && (
              <Link to="/finance" className="flex items-center justify-between rounded-lg border border-warning/20 bg-warning/10 p-2.5 text-sm" data-testid="finance-pending-link">
                <span className="text-warning">Pending payment requests: {summary.finance.pending_payment_requests} ({fmtCurrency(summary.finance.pending_payment_amount)})</span>
                <ArrowRight className="size-3" />
              </Link>
            )}
          </CardContent>
        </Card>

        <Card data-testid="dashboard-exception-panel">
          <CardHeader>
            <CardTitle>Management attention</CardTitle>
            <CardDescription>Items requiring action from validated source data</CardDescription>
          </CardHeader>
          <CardContent className="space-y-1">
            {exceptionItems.length === 0 ? (
              <div className="rounded-lg border border-success/20 bg-success/10 p-3 text-sm text-success" data-testid="no-exceptions">
                No exceptions detected. All clear.
              </div>
            ) : (
              exceptionItems.map((item) => (
                <Link key={item.label} to={item.link} className="flex items-center justify-between rounded-lg px-2 py-2.5 hover:bg-secondary/45" data-testid={`exception-${item.label.toLowerCase().replace(/\s+/g, "-")}`}>
                  <div className="flex items-center gap-2">
                    <AlertTriangle className="size-4 text-warning" />
                    <span className="text-sm">{item.label}</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <span className="font-semibold">{item.value}</span>
                    <ArrowRight className="size-3 text-muted-foreground" />
                  </div>
                </Link>
              ))
            )}
          </CardContent>
        </Card>
      </section>

      <section className="grid gap-4 sm:grid-cols-2 xl:grid-cols-4" data-testid="dashboard-secondary-kpis">
        <Link to="/inventory">
          <MetricCard label="Inventory Items" value={fmt(summary.inventory.item_count)} detail={`total stock: ${fmt(summary.inventory.total_stock)}`} trend="" icon={PackageOpen} tone="neutral" testId="dashboard-kpi-inventory-items" />
        </Link>
        <Link to="/distributions">
          <MetricCard label="Pending Distributions" value={fmt(summary.inventory.pending_distributions)} detail="DRAFT, not issued" trend="" icon={PackageOpen} tone="warning" testId="dashboard-kpi-pending-distributions" />
        </Link>
        <Link to="/agreements">
          <MetricCard label="Agreements Expiring" value={fmt(summary.agreements.expiring_soon)} detail="within 90 days" trend="" icon={FileCheck2} tone="warning" testId="dashboard-kpi-agreements-expiring" />
        </Link>
        <Link to="/assets">
          <MetricCard label="Total Assets" value={fmt(summary.assets.total)} detail={Object.entries(summary.assets.by_status).map(([k, v]) => `${k.toLowerCase()}:${v}`).join(", ")} trend="" icon={Archive} tone="neutral" testId="dashboard-kpi-total-assets" />
        </Link>
      </section>
    </div>
  );
}
